'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetCreatedMarketsAndProposalsRow, useGetProposalsField } from '@morfi/backend-sdk-v2/client';
import { useFormatter, useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ProposalShareButton } from '@/components/launchpad/proposal-share-button';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { ShareButton } from '@/components/market/share-button';
import { AnimatedBigInt } from '@/components/ui/animated-bigint';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem } from '@/components/ui/data-list-item';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency/use-formatted-currency';
export function CreatedProposalRow(t0) {
  const $ = _c(45);
  const {
    pubkey,
    snapshot
  } = t0;
  const t = useTranslations("profile.created");
  const tStatus = useTranslations("launchpad.card");
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  const staked = useGetProposalsField(pubkey, "totalStaked");
  const status = useGetProposalsField(pubkey, "status");
  const proposal = snapshot?.proposal;
  const graduated = proposal?.graduatedMarketPubkey;
  const href = graduated ? `/markets/${graduated}` : `/proposals/${pubkey}`;
  const intent = useIntentRoutePrefetch(href);
  let t1;
  if ($[0] !== formatD6) {
    t1 = function formatAmount(value) {
      return formatD6(value.toString());
    };
    $[0] = formatD6;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formatAmount = t1;
  if (proposal == null) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Skeleton className="h-24 w-full rounded-control" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  const state = status ?? proposal.status;
  const statusKey = state === "active" || state === "needs_debate" ? "statusActive" : state === "creating" ? "graduating" : state === "live" ? "statusApprovedLive" : state === "rejected" ? "statusRejected" : "statusPending";
  const t2 = proposal.title;
  const t3 = proposal.title;
  let t4;
  if ($[3] !== statusKey || $[4] !== tStatus) {
    t4 = tStatus(statusKey);
    $[3] = statusKey;
    $[4] = tStatus;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = proposal.createdAt;
  let t6;
  if ($[6] !== formatter || $[7] !== proposal.createdAt) {
    t6 = formatter.dateTime(new Date(proposal.createdAt), {
      dateStyle: "medium",
      timeZone: "UTC"
    });
    $[6] = formatter;
    $[7] = proposal.createdAt;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== proposal.createdAt || $[10] !== t6) {
    t7 = <time dateTime={t5}>{t6}</time>;
    $[9] = proposal.createdAt;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <>{t4} ·{" "}{t7}</>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = proposal.coverImageUrl ?? null;
  let t10;
  if ($[15] !== proposal.category || $[16] !== proposal.coverImageNsfw || $[17] !== pubkey || $[18] !== t9) {
    t10 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey,
      category: proposal.category,
      coverImageUrl: t9,
      nsfw: proposal.coverImageNsfw
    }} alt="" size="size-12" rounded="rounded-control" preview={false} />;
    $[15] = proposal.category;
    $[16] = proposal.coverImageNsfw;
    $[17] = pubkey;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== proposal.marketType || $[21] !== t10) {
    t11 = <ThumbWithOrigin marketType={proposal.marketType}>{t10}</ThumbWithOrigin>;
    $[20] = proposal.marketType;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t("totalBacked");
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = staked ?? proposal.totalStaked;
  let t14;
  if ($[25] !== t13) {
    t14 = BigInt(t13);
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== formatAmount || $[28] !== t14) {
    t15 = <AnimatedBigInt value={t14} format={formatAmount} />;
    $[27] = formatAmount;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t12 || $[31] !== t15) {
    t16 = [{
      key: "backed",
      label: t12,
      value: t15
    }];
    $[30] = t12;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== graduated || $[34] !== proposal.title || $[35] !== pubkey) {
    t17 = graduated ? <ShareButton marketPubkey={graduated} marketTitle={proposal.title} /> : <ProposalShareButton proposalPubkey={pubkey} proposalTitle={proposal.title} iconOnly={true} />;
    $[33] = graduated;
    $[34] = proposal.title;
    $[35] = pubkey;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== href || $[38] !== intent || $[39] !== proposal.title || $[40] !== t11 || $[41] !== t16 || $[42] !== t17 || $[43] !== t8) {
    t18 = <DataListItem href={href} intent={intent} ariaLabel={t2} primaryText={t3} secondaryText={t8} image={t11} metrics={t16} action={t17} />;
    $[37] = href;
    $[38] = intent;
    $[39] = proposal.title;
    $[40] = t11;
    $[41] = t16;
    $[42] = t17;
    $[43] = t8;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
}